import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import AnalysisCandlestickChart from '../components/AnalysisCandlestickChart';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Crown, Loader2, RefreshCw, Save, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

const INTERVAL_LABELS = { '1d': 'Günlük', '1wk': 'Haftalık', '1mo': 'Aylık' };

const PlatinumAnalysisPage = () => {
  const { getAuthHeader, user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [loading, setLoading] = useState(true);
  const [bulletin, setBulletin] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [refCandles, setRefCandles] = useState([]);
  const [simCandles, setSimCandles] = useState([]);
  const [refLoading, setRefLoading] = useState(false);
  const [simLoading, setSimLoading] = useState(false);
  const [interval, setInterval_] = useState('1wk');

  const [adminOpen, setAdminOpen]     = useState(false);
  const [editComment, setEditComment] = useState('');
  const [saving, setSaving]           = useState(false);
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [deleting, setDeleting]       = useState(false);

  useEffect(() => {
    fetchBulletin();
  }, []);

  const fetchBulletin = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await axios.get(`${API_URL}/bulletin/simons`, { headers: getAuthHeader() });
      if (!res.data) {
        setNotFound(true);
        return;
      }
      setBulletin(res.data);
      setEditComment(res.data.admin_comment || '');
      const iv = res.data.interval || '1wk';
      setInterval_(iv);
      if (res.data.reference_symbol && res.data.similar_symbol) {
        fetchCandles(res.data.reference_symbol, res.data.similar_symbol, iv);
      }
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 403) {
        setNotFound(true);
      } else {
        toast.error('Bülten yüklenemedi.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCandles = async (refSymbol, simSymbol, iv) => {
    setRefLoading(true);
    setSimLoading(true);
    try {
      const [refRes, simRes] = await Promise.all([
        axios.get(`${API_URL}/stocks/${refSymbol}/candlestick?interval=${iv}&period=7y`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/stocks/${simSymbol}/candlestick?interval=${iv}&period=7y`, { headers: getAuthHeader() }),
      ]);
      setRefCandles(refRes.data.candles || []);
      setSimCandles(simRes.data.candles || []);
    } catch {
      toast.error('Grafik verisi yüklenemedi.');
    } finally {
      setRefLoading(false);
      setSimLoading(false);
    }
  };

  const handleIntervalChange = (iv) => {
    setInterval_(iv);
    if (bulletin) fetchCandles(bulletin.reference_symbol, bulletin.similar_symbol, iv);
  };

  const handleSaveComment = async () => {
    if (!bulletin) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/bulletin`, {
        bulletin_type: 'simons',
        reference_symbol: bulletin.reference_symbol,
        similar_symbol:   bulletin.similar_symbol,
        interval:         bulletin.interval,
        start_date:       bulletin.start_date,
        end_date:         bulletin.end_date,
        admin_comment:    editComment,
      }, { headers: getAuthHeader() });
      toast.success('Bülten güncellendi.');
      fetchBulletin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/admin/bulletin/simons`, { headers: getAuthHeader() });
      toast.success('Bülten silindi.');
      setBulletin(null);
      setNotFound(true);
      setDeleteOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi.');
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return null;
    try {
      return format(new Date(isoStr), 'd MMMM yyyy', { locale: tr });
    } catch {
      return isoStr;
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F6F1EA]">
      <Sidebar />
      <main className="flex-1 lg:ml-64 px-4 sm:px-6 lg:px-8 py-8 mt-14 lg:mt-0">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#8f969e]/20 flex items-center justify-center">
              <Crown className="w-5 h-5 text-[#3c3e42]" />
            </div>
            <div>
              <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#2E2620]">
                Simons Analizi
              </h1>
              <p className="text-sm text-[#7A6A5C]">
                Platinum üyelere özel aylık hisse tavsiyesi ve karşılaştırma analizi.
              </p>
            </div>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-[#7A6A5C] py-12 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Bülten yükleniyor...</span>
            </div>
          )}

          {!loading && notFound && (
            <Card className="border-[#E6DCCF] bg-white">
              <CardContent className="py-16 text-center">
                <Crown className="w-10 h-10 text-[#8f969e] mx-auto mb-3 opacity-50" />
                <p className="text-[#7A6A5C] text-sm">
                  Bu ay için henüz bir bülten yayınlanmamış. Yakında eklenecek.
                </p>
                <Button
                  variant="ghost"
                  onClick={fetchBulletin}
                  className="mt-4 text-[#C86F4A] hover:text-[#B05D3A]"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Yenile
                </Button>
              </CardContent>
            </Card>
          )}

          {!loading && bulletin && (
            <>
              {/* Bulletin meta */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 bg-[#8f969e]/15 text-[#3c3e42] text-xs font-semibold px-3 py-1.5 rounded-full">
                    <Crown className="w-3.5 h-3.5" />
                    Platinum Bülten
                  </span>
                  {bulletin.updated_at && (
                    <span className="text-xs text-[#7A6A5C]">
                      Son güncelleme: {formatDate(bulletin.updated_at)}
                    </span>
                  )}
                </div>

                {bulletin.reference_symbol && bulletin.similar_symbol && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#7A6A5C]">Zaman aralığı:</span>
                    <Select value={interval} onValueChange={handleIntervalChange}>
                      <SelectTrigger className="w-32 border-[#E6DCCF] text-sm h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(INTERVAL_LABELS).map(([val, label]) => (
                          <SelectItem key={val} value={val}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Hisse başlıkları ve grafikler — sadece semboller girilmişse göster */}
              {bulletin.reference_symbol && bulletin.similar_symbol && (
                <>
                  {/* Stock header */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Card className="border-[#E6DCCF] bg-white">
                      <CardHeader className="pb-2 pt-4 px-4">
                        <CardTitle className="text-base text-[#2E2620]">
                          Referans Hisse
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pb-4 px-4">
                        <p className="text-2xl font-bold text-[#2E2620] font-['Playfair_Display']">
                          {bulletin.reference_symbol}
                        </p>
                        {(bulletin.start_date || bulletin.end_date) && (
                          <p className="text-xs text-[#7A6A5C] mt-1">
                            {bulletin.start_date && formatDate(bulletin.start_date)}
                            {bulletin.start_date && bulletin.end_date && ' → '}
                            {bulletin.end_date && formatDate(bulletin.end_date)}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                    <Card className="border-[#E6DCCF] bg-white">
                      <CardHeader className="pb-2 pt-4 px-4">
                        <CardTitle className="text-base text-[#2E2620]">
                          Benzer Hisse (Tavsiye)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pb-4 px-4">
                        <p className="text-2xl font-bold text-[#C86F4A] font-['Playfair_Display']">
                          {bulletin.similar_symbol}
                        </p>
                        {(bulletin.start_date || bulletin.end_date) && (
                          <p className="text-xs text-[#7A6A5C] mt-1">
                            {bulletin.start_date && formatDate(bulletin.start_date)}
                            {bulletin.start_date && bulletin.end_date && ' → '}
                            {bulletin.end_date && formatDate(bulletin.end_date)}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Charts */}
                  <div className="space-y-4">
                    <Card className="border-[#E6DCCF]">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold text-[#2E2620]">
                          {bulletin.reference_symbol} — Grafik
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <AnalysisCandlestickChart
                          data={refCandles}
                          loading={refLoading}
                          height={300}
                          visibleStartDate={bulletin.start_date || null}
                          visibleEndDate={bulletin.end_date || null}
                          highlightStartDate={bulletin.start_date || null}
                          highlightEndDate={bulletin.end_date || null}
                        />
                      </CardContent>
                    </Card>

                    <Card className="border-[#E6DCCF]">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold text-[#2E2620]">
                          {bulletin.similar_symbol} — Grafik
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <AnalysisCandlestickChart
                          data={simCandles}
                          loading={simLoading}
                          height={300}
                        />
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}

              {/* Admin comment */}
              {bulletin.admin_comment && (
                <Card className="border-[#E6DCCF] bg-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#2E2620]">
                      Analist Yorumu
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#2E2620] leading-relaxed whitespace-pre-line">
                      {bulletin.admin_comment}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Admin araçları */}
              {isAdmin && (
                <Card className="border-[#C86F4A]/30 bg-[#C86F4A]/5">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <button
                      onClick={() => setAdminOpen(o => !o)}
                      className="flex items-center justify-between w-full text-left"
                    >
                      <CardTitle className="text-sm font-semibold text-[#C86F4A]">Admin — Bülten Yönetimi</CardTitle>
                      {adminOpen ? <ChevronUp className="w-4 h-4 text-[#C86F4A]" /> : <ChevronDown className="w-4 h-4 text-[#C86F4A]" />}
                    </button>
                  </CardHeader>
                  {adminOpen && (
                    <CardContent className="px-4 pb-4 space-y-3">
                      <div className="space-y-1.5">
                        <Label className="text-[#2E2620] text-xs">Analist Yorumu</Label>
                        <Textarea
                          value={editComment}
                          onChange={e => setEditComment(e.target.value)}
                          placeholder="Kullanıcılara gösterilecek analiz yorumunu yazın..."
                          rows={4}
                          className="border-[#E6DCCF] focus-visible:ring-[#C86F4A] resize-none text-sm"
                        />
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={handleSaveComment}
                          disabled={saving}
                          className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full"
                        >
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                          Bültene Koy
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteOpen(true)}
                          className="border-red-200 text-red-600 hover:bg-red-50 rounded-full"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          Bülteni Sil
                        </Button>
                      </div>
                    </CardContent>
                  )}
                </Card>
              )}

              <p className="text-[10px] text-[#7A6A5C] leading-relaxed border-t border-[#E6DCCF] pt-4">
                Bu içerik SPK mevzuatı kapsamında yatırım danışmanlığı hizmeti değildir. Paylaşılan analizler kişisel değerlendirme niteliğindedir.
                Sermaye piyasalarında değer kaybı ve zarar riski bulunmaktadır. İşlem kararlarınızın sorumluluğu size aittir.
              </p>
            </>
          )}
        </div>
      </main>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="border-[#E6DCCF] bg-[#F6F1EA]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#2E2620] font-['Playfair_Display']">Bülteni sil?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A6A5C]">
              Simons Analizi bülteni kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E6DCCF]">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PlatinumAnalysisPage;
